import { useCallback, useEffect, useMemo, useState } from 'react'

import { selectDamagePlayer, selectHasStartedQuiz, setDamagePlayer } from '~/bootcamp/slices/bootcamp-quiz.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import useAvatarSrc from '~/shared/hooks/useAvatarSrc'

import { QuizState } from '~/bootcamp/components/Quiz/QuizData.models'
import useCurrentUser from '~/hooks/useCurrentUser'
import { getUserAge } from '~/auth/utils/register.utils'
import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'
import { OutfitGender } from '~/homebase/types/Homebase.types'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

const mediaQueryAvatar = [{ screen: '(min-width: 280px)', width: '160px' }]
const mediaQueryHeart = [{ screen: '(min-width: 280px)', width: '35px' }]
export default function PlayerHealth({
  quizState,
  className,
}: {
  quizState: QuizState
  className?: string
}): React.ReactElement | null {
  const remaningHearts = useMemo(
    () => quizState.maxWrongAnswer + 1 - quizState.wrongAnswer,
    [quizState.maxWrongAnswer, quizState.wrongAnswer],
  )
  const [playerHealth, setPlayerHealth] = useState(remaningHearts)
  const avatar = useAvatarSrc()
  const dispatch = useAppDispatch()
  const isQuizStarted = useAppSelector(selectHasStartedQuiz)
  const damagePlayer = useAppSelector(selectDamagePlayer)

  const user = useCurrentUser()
  const { data: metadata } = useGetAvatarMetadataQuery(user, { skip: !user?.avatarUrl })

  const isFemale = Boolean(metadata?.outfitGender !== OutfitGender.MASCULINE)

  const refillHarts = useCallback(() => {
    setPlayerHealth(remaningHearts)
  }, [remaningHearts])

  useEffect(() => {
    if (damagePlayer) {
      setPlayerHealth(remaningHearts)
      dispatch(setDamagePlayer(false))
    }
  }, [damagePlayer, dispatch, remaningHearts])

  if (!isQuizStarted) return null
  if (remaningHearts > playerHealth) refillHarts()

  const age = getUserAge(user)

  const under13 = !age || age < 13

  return (
    <div className={`flex items-center pt-2 ${className}`}>
      <div className="relative flex items-center justify-center rounded-full border border-white w-20 h-20 overflow-hidden">
        <ResponsiveImage
          fit="contain"
          height={160}
          imageClassName={`relative rounded-full object-contain h-40 ${
            under13 ? 'min-w-25 mt-5' : isFemale ? 'min-w-30 mt-2' : 'min-w-40 mt-12'
          }`}
          imageWidths="160"
          mediaQuery={mediaQueryAvatar}
          srcKey={avatar}
          width={160}
        />
      </div>
      <div className="flex items-center flex-wrap gap-x-0.5 px-1 max-w-52 object-scale-down">
        {typeof quizState.wrongAnswer === 'number' &&
          [...Array(quizState.maxWrongAnswer + 1)].map((e, i) => (
            <ResponsiveImage
              fit="contain"
              height={27}
              imageClassName={i + 1 > playerHealth ? 'grayscale' : ''}
              imageWidths="35"
              key={`heart${i.toString()}`}
              mediaQuery={mediaQueryHeart}
              srcKey="bootcamp/heart-v04.png"
              width={35}
            />
          ))}
      </div>
    </div>
  )
}
